import React, { useState, useEffect, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Box,
  Card,
  CardContent,
  Grid,
  Chip,
  CircularProgress,
  Alert,
  Stack,
  Divider,
  IconButton,
  Tooltip,
  Collapse,
  TextField
} from '@mui/material';
import {
  Close as CloseIcon,
  AccessTime as AccessTimeIcon,
  MonetizationOn as MonetizationOnIcon,
  CalendarToday as CalendarIcon,
  TrendingUp as TrendingUpIcon,
  Refresh as RefreshIcon,
  KeyboardArrowDown as ExpandIcon,
  KeyboardArrowUp as CollapseIcon,
  Edit as EditIcon,
  RestartAlt as RestoreIcon
} from '@mui/icons-material';
import { getPeriodSummaries, adjustAutoBonus, resetAutoBonus } from '../../services/livestreamService';
import { monthLabel } from '../dashboard/theme';

const UserEarningsHistory = ({ 
  open, 
  onClose, 
  userId, 
  userName, 
  showNotification,
  isMobile 
}) => {
  const [periodSummaries, setPeriodSummaries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [expandedId, setExpandedId] = useState(null); // kỳ đang mở chi tiết
  // Điều chỉnh thưởng tự động: { summaryId, type, label, current, original, note }
  const [adjusting, setAdjusting] = useState(null);
  const [savingAdjust, setSavingAdjust] = useState(false);

  const openAdjust = (period, b) => setAdjusting({
    summaryId: period._id,
    type: b.type,
    label: b.reason || b.type,
    amount: String(b.adjusted && b.originalAmount != null ? b.amount : b.amount),
    original: b.originalAmount != null ? b.originalAmount : b.amount,
    isAdjusted: !!b.adjusted,
    note: ''
  });

  const saveAdjust = async () => {
    if (!adjusting) return;
    const num = Number(adjusting.amount);
    if (!Number.isFinite(num) || num < 0) {
      showNotification?.('Số tiền không hợp lệ (điền 0 để bỏ khoản này)', 'error');
      return;
    }
    setSavingAdjust(true);
    const res = await adjustAutoBonus(adjusting.summaryId, adjusting.type, { amount: num, note: adjusting.note });
    setSavingAdjust(false);
    if (res.success) {
      showNotification?.(res.message || 'Đã điều chỉnh', 'success');
      setAdjusting(null);
      fetchEarningsHistory();
    } else {
      showNotification?.(res.error?.message || 'Lỗi điều chỉnh', 'error');
    }
  };

  const handleReset = async (period, type) => {
    const res = await resetAutoBonus(period._id, type);
    if (res.success) {
      showNotification?.(res.message || 'Đã khôi phục', 'success');
      fetchEarningsHistory();
    } else {
      showNotification?.(res.error?.message || 'Lỗi khôi phục', 'error');
    }
  };

  // Fetch lịch sử thu nhập khi dialog mở
  useEffect(() => {
    if (open && userId) {
      fetchEarningsHistory();
    }
  }, [open, userId]);

  const fetchEarningsHistory = async () => {
    setLoading(true);
    setError(null);
    try {
      console.log('🔍 Fetching earnings history for userId:', userId);
      const result = await getPeriodSummaries('all', userId);
      console.log('📊 Earnings history result:', result);
      
      if (result.success) {
        const summaries = result.data?.data || result.data || [];
        console.log('✅ Period summaries:', summaries);
        setPeriodSummaries(Array.isArray(summaries) ? summaries : []);
      } else {
        setError(result.error?.message || 'Không thể lấy lịch sử thu nhập');
        showNotification(result.error?.message || 'Không thể lấy lịch sử thu nhập', 'error');
      }
    } catch (error) {
      console.error('🚨 Error fetching earnings history:', error);
      setError('Có lỗi xảy ra khi lấy dữ liệu');
      showNotification('Có lỗi xảy ra khi lấy dữ liệu', 'error');
    } finally {
      setLoading(false);
    }
  };

  // Tính toán thống kê tổng quan
  const stats = useMemo(() => {
    if (!periodSummaries.length) {
      return {
        totalPeriods: 0,
        totalHours: 0,
        totalEarnings: 0,
        averageHoursPerPeriod: 0,
        averageEarningsPerPeriod: 0
      };
    }

    const totalHours = periodSummaries.reduce((sum, period) => sum + (period.totalHours || 0), 0);
    const totalEarnings = periodSummaries.reduce((sum, period) => sum + (period.totalEarnings || 0), 0);

    return {
      totalPeriods: periodSummaries.length,
      totalHours,
      totalEarnings,
      averageHoursPerPeriod: Math.round(totalHours / periodSummaries.length * 100) / 100,
      averageEarningsPerPeriod: Math.round(totalEarnings / periodSummaries.length)
    };
  }, [periodSummaries]);

  // Format tiền tệ Việt Nam
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('vi-VN', {
      style: 'currency',
      currency: 'VND'
    }).format(amount || 0);
  };

  // Format ngày tháng
  const formatDate = (date) => {
    return new Date(date).toLocaleDateString('vi-VN', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  // Lấy tên kỳ (T4, T5, T6, ...)
  const getPeriodName = (periodStart) => {
    const date = new Date(periodStart);
    const month = date.getMonth() + 1;
    return `T${month}`;
  };

  // Hiển thị các khoản theo chính sách mới (thưởng auto / khấu trừ / tỷ trọng TikTok)
  const renderPolicyExtras = (period) => {
    const autoB = period.totalAutoBonus || 0;
    const ded = period.totalDeduction || 0;
    const grand = period.grandTotal ?? period.totalEarnings;
    const ratio = period.leaderBonusBreakdown?.tiktokRatio;
    if (!autoB && !ded && grand === period.totalEarnings && !ratio && !(period.autoBonuses?.length) && !(period.deductions?.length)) return null;
    return (
      <Stack spacing={0.3} sx={{ mt: 0.5, alignItems: 'flex-end' }}>
        {autoB > 0 && <Chip size="small" color="success" variant="outlined" label={`+Thưởng: ${formatCurrency(autoB)}`} />}
        {ded > 0 && <Chip size="small" color="error" variant="outlined" label={`-Khấu trừ: ${formatCurrency(ded)}`} />}
        {!!ratio && <Chip size="small" color="info" variant="outlined" label={`Tỷ trọng TikTok: ${ratio}%`} />}
        {period.minHoursMet === false && <Chip size="small" color="warning" variant="filled" label={`Thiếu giờ (KPI ${period.kpiHours || 0}h)`} />}
        {(autoB > 0 || ded > 0) && (
          <Tooltip title={[
            ...(period.autoBonuses || []).map(b => `+ ${b.reason}: ${formatCurrency(b.amount)}`),
            ...(period.deductions || []).map(d => `- ${d.reason}: ${formatCurrency(d.amount)}`)
          ].join('\n')}>
            <Typography variant="caption" fontWeight={700} color="primary.main" sx={{ cursor: 'help' }}>
              Thực nhận: {formatCurrency(grand)}
            </Typography>
          </Tooltip>
        )}
      </Stack>
    );
  };

  // Breakdown CHI TIẾT: lương cứng, hoa hồng %, thưởng leader (HH team/tăng trưởng/KPI), thưởng auto, khấu trừ → thực nhận
  const renderBreakdown = (period) => {
    const lb = period.leaderBonusBreakdown || {};
    const Row = ({ label, value, color = 'text.primary', sub, bold = false }) => (
      <Box>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 1 }}>
          <Typography variant="body2" sx={{ color, fontWeight: bold ? 700 : 400 }}>{label}</Typography>
          <Typography variant="body2" sx={{ color, fontWeight: bold ? 700 : 600, whiteSpace: 'nowrap' }}>{value}</Typography>
        </Box>
        {sub && <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.35 }}>{sub}</Typography>}
      </Box>
    );
    // % hoa hồng team: ưu tiên field đã lưu; data cũ thì suy từ teamCommission/teamTotalGMV
    const teamPct = lb.teamCommissionPercent != null && lb.teamCommissionPercent > 0
      ? lb.teamCommissionPercent
      : (lb.teamTotalGMV > 0 ? Math.round((lb.teamCommission / lb.teamTotalGMV) * 10000) / 100 : 0);
    // 1 dòng thành phần thưởng leader: nhãn + số (tím) + công thức dẫn chứng bên dưới
    const LeaderLine = ({ label, value, formula }) => (
      <Box sx={{ mt: 0.5 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 1 }}>
          <Typography variant="caption" sx={{ color: '#334155', fontWeight: 600 }}>{label}</Typography>
          <Typography variant="caption" sx={{ color: '#7c3aed', fontWeight: 700, whiteSpace: 'nowrap' }}>{value}</Typography>
        </Box>
        {formula && <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontSize: '0.7rem', lineHeight: 1.35 }}>{formula}</Typography>}
      </Box>
    );
    return (
      <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, bgcolor: '#fafcff' }}>
        <Typography variant="caption" fontWeight={700} color="primary.main" sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
          Chi tiết cấu thành thu nhập
        </Typography>
        <Stack spacing={0.75} divider={<Divider flexItem sx={{ opacity: 0.5 }} />} sx={{ mt: 1 }}>
          {period.baseSalary > 0 && <Row label="Lương cứng" value={formatCurrency(period.baseSalary)} />}
          {period.liveEarnings > 0 && <Row label="Thu nhập giờ live" value={formatCurrency(period.liveEarnings)} />}
          {period.commissionAmount > 0 && (
            <Row
              label={`Hoa hồng${period.effectiveCommissionRate ? ` (${period.effectiveCommissionRate}%)` : ''}`}
              value={formatCurrency(period.commissionAmount)}
              sub={`GMV ${formatCurrency(period.totalGMV)} — Shopee ${formatCurrency(period.gmvShopee)} · TikTok ${formatCurrency(period.gmvTiktok)}`}
            />
          )}
          {period.leaderBonus > 0 && (
            <Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 1 }}>
                <Typography variant="body2">Thưởng quản lý (leader)</Typography>
                <Typography variant="body2" fontWeight={600}>{formatCurrency(period.leaderBonus)}</Typography>
              </Box>
              <Box sx={{ ml: 1, mt: 0.5, pl: 1.25, borderLeft: '2px solid #ede9fe' }}>
                {lb.teamCommission > 0 && (
                  <LeaderLine
                    label={`Hoa hồng team (${teamPct}%)`}
                    value={formatCurrency(lb.teamCommission)}
                    formula={`${teamPct}% × GMV team ${formatCurrency(lb.teamTotalGMV)}`}
                  />
                )}
                {lb.growthBonus > 0 && (
                  <LeaderLine
                    label={`Thưởng tăng trưởng${lb.growthBonusPercent ? ` (${lb.growthBonusPercent}%)` : ''}`}
                    value={formatCurrency(lb.growthBonus)}
                    formula={lb.growthAmount
                      ? (lb.growthBaseline === 'peak'
                        ? `${lb.growthBonusPercent}% × phần vượt kỷ lục ${formatCurrency(lb.growthAmount)}  (kỷ lục 6 tháng ${monthLabel(lb.baselineTeamMonth)} ${formatCurrency(lb.baselineTeamGMV)} → nay ${formatCurrency(lb.teamTotalGMV)})`
                        : `${lb.growthBonusPercent}% × mức tăng ${formatCurrency(lb.growthAmount)}  (GMV team kỳ trước ${formatCurrency(lb.prevTeamTotalGMV)} → nay ${formatCurrency(lb.teamTotalGMV)})`)
                      : null}
                  />
                )}
                {lb.tiktokKpiBonus > 0 && (
                  <LeaderLine
                    label={`KPI tăng trưởng TikTok (${lb.tiktokKpiBonusPercent}%)`}
                    value={formatCurrency(lb.tiktokKpiBonus)}
                    formula={lb.tiktokCurrentGMV != null
                      ? (lb.growthBaseline === 'peak'
                        ? `${lb.tiktokKpiBonusPercent}% × phần vượt kỷ lục  (kỷ lục TikTok 6 tháng ${monthLabel(lb.baselineTiktokMonth)} ${formatCurrency(lb.baselineTiktokGMV)} → nay ${formatCurrency(lb.tiktokCurrentGMV)}, +${lb.tiktokGrowthPercent}%)`
                        : `GMV TikTok team ${formatCurrency(lb.tiktokPrevGMV)} → ${formatCurrency(lb.tiktokCurrentGMV)} (${lb.tiktokGrowthPercent}%)`)
                      : null}
                  />
                )}
                {lb.tiktokRatioBonus > 0 && (
                  <LeaderLine
                    label="Thưởng tỷ trọng TikTok"
                    value={formatCurrency(lb.tiktokRatioBonus)}
                    formula={`Tỷ trọng ${lb.tiktokRatio}% (TikTok ${formatCurrency(lb.tiktokCurrentGMV)} / GMV team ${formatCurrency(lb.teamTotalGMV)})`}
                  />
                )}
                {lb.tiktokRatio != null && lb.tiktokRatioBonus === 0 && lb.tiktokCurrentGMV != null && (
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5, fontSize: '0.7rem', lineHeight: 1.35 }}>
                    Tỷ trọng TikTok: {lb.tiktokRatio}% (TikTok {formatCurrency(lb.tiktokCurrentGMV)} / GMV team {formatCurrency(lb.teamTotalGMV)}) — chưa đạt mốc thưởng tỷ trọng
                  </Typography>
                )}
              </Box>
            </Box>
          )}
          {(period.autoBonuses || []).map((b, i) => (
            <Box key={`a${i}`}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 1 }}>
                <Typography variant="body2" sx={{ color: 'success.main' }}>
                  + {b.reason || 'Thưởng chính sách'}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25, whiteSpace: 'nowrap' }}>
                  <Typography variant="body2" sx={{ color: 'success.main', fontWeight: 600 }}>
                    + {formatCurrency(b.amount)}
                  </Typography>
                  <Tooltip title="Sửa số tiền hoặc bỏ khoản này">
                    <IconButton size="small" sx={{ p: 0.25 }} onClick={() => openAdjust(period, b)}>
                      <EditIcon sx={{ fontSize: 15 }} />
                    </IconButton>
                  </Tooltip>
                  {b.adjusted && (
                    <Tooltip title="Khôi phục về số hệ thống tính">
                      <IconButton size="small" sx={{ p: 0.25 }} onClick={() => handleReset(period, b.type)}>
                        <RestoreIcon sx={{ fontSize: 15 }} />
                      </IconButton>
                    </Tooltip>
                  )}
                </Box>
              </Box>
              {b.adjusted && b.originalAmount != null && (
                <Typography variant="caption" color="warning.main" sx={{ display: 'block', lineHeight: 1.35 }}>
                  Đã điều chỉnh — hệ thống tính {formatCurrency(b.originalAmount)}
                </Typography>
              )}
            </Box>
          ))}
          {/* Khoản bị bỏ hẳn không còn trong autoBonuses → hiện riêng để biết mà khôi phục */}
          {(period.autoBonusAdjustments || []).filter(a => (a.amount || 0) <= 0).map((a, i) => (
            <Box key={`x${i}`} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 1 }}>
              <Typography variant="body2" color="text.disabled" sx={{ textDecoration: 'line-through' }}>
                {a.type}{a.note ? ` — ${a.note}` : ''}
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25, whiteSpace: 'nowrap' }}>
                <Typography variant="caption" color="text.disabled">đã bỏ</Typography>
                <Tooltip title="Khôi phục khoản này">
                  <IconButton size="small" sx={{ p: 0.25 }} onClick={() => handleReset(period, a.type)}>
                    <RestoreIcon sx={{ fontSize: 15 }} />
                  </IconButton>
                </Tooltip>
              </Box>
            </Box>
          ))}
          {(period.bonuses || []).map((b, i) => (
            <Row key={`m${i}`} label={`+ Thưởng thêm${b.reason ? `: ${b.reason}` : ''}`} value={`+ ${formatCurrency(b.amount)}`} color="#f57c00" />
          ))}
          {(period.deductions || []).map((d, i) => (
            <Row key={`d${i}`} label={`− ${d.reason || 'Khấu trừ'}${d.auto === false ? ' (thủ công)' : ''}`} value={`− ${formatCurrency(d.amount)}`} color="error.main" />
          ))}
          <Row label="Thực nhận" value={formatCurrency(period.grandTotal ?? period.totalEarnings)} bold color="primary.main" />
        </Stack>
        <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap sx={{ mt: 1.5 }}>
          <Chip size="small" variant="outlined" label={`KPI: ${period.kpiHours ?? period.totalHours ?? 0}h`} />
          {typeof period.minHoursMet === 'boolean' && (
            <Chip size="small" color={period.minHoursMet ? 'success' : 'warning'} variant={period.minHoursMet ? 'outlined' : 'filled'}
              label={period.minHoursMet ? 'Đủ giờ tối thiểu' : 'Chưa đủ giờ tối thiểu'} />
          )}
          {period.nightShiftCount > 0 && <Chip size="small" variant="outlined" label={`Ca đêm: ${period.nightShiftCount}`} />}
          {(period.warnings || []).map((w, i) => (
            <Chip key={`w${i}`} size="small" color="warning" variant="outlined" label={w} sx={{ maxWidth: '100%', height: 'auto', '& .MuiChip-label': { whiteSpace: 'normal', py: 0.4 } }} />
          ))}
        </Stack>
      </Paper>
    );
  };

  // Render thống kê tổng quan
  const renderStatsCards = () => (
    <Grid container spacing={2} sx={{ mb: 3 }}>
      <Grid item xs={6} sm={3}>
        <Card sx={{ textAlign: 'center', bgcolor: '#e3f2fd' }}>
          <CardContent sx={{ py: 2 }}>
            <CalendarIcon sx={{ color: '#1976d2', fontSize: 24, mb: 1 }} />
            <Typography variant="h6" sx={{ color: '#1976d2', fontWeight: 'bold' }}>
              {stats.totalPeriods}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Tổng số kỳ
            </Typography>
          </CardContent>
        </Card>
      </Grid>
      <Grid item xs={6} sm={3}>
        <Card sx={{ textAlign: 'center', bgcolor: '#f3e5f5' }}>
          <CardContent sx={{ py: 2 }}>
            <AccessTimeIcon sx={{ color: '#7b1fa2', fontSize: 24, mb: 1 }} />
            <Typography variant="h6" sx={{ color: '#7b1fa2', fontWeight: 'bold' }}>
              {stats.totalHours}h
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Tổng giờ live
            </Typography>
          </CardContent>
        </Card>
      </Grid>
      <Grid item xs={6} sm={3}>
        <Card sx={{ textAlign: 'center', bgcolor: '#e8f5e8' }}>
          <CardContent sx={{ py: 2 }}>
            <MonetizationOnIcon sx={{ color: '#388e3c', fontSize: 24, mb: 1 }} />
            <Typography variant="h6" sx={{ color: '#388e3c', fontWeight: 'bold' }}>
              {formatCurrency(stats.totalEarnings)}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Tổng thu nhập
            </Typography>
          </CardContent>
        </Card>
      </Grid>
      <Grid item xs={6} sm={3}>
        <Card sx={{ textAlign: 'center', bgcolor: '#fff3e0' }}>
          <CardContent sx={{ py: 2 }}>
            <TrendingUpIcon sx={{ color: '#f57c00', fontSize: 24, mb: 1 }} />
            <Typography variant="h6" sx={{ color: '#f57c00', fontWeight: 'bold' }}>
              {stats.averageHoursPerPeriod}h
            </Typography>
            <Typography variant="caption" color="text.secondary">
              TB giờ/kỳ
            </Typography>
          </CardContent>
        </Card>
      </Grid>
    </Grid>
  );

  // Render bảng lịch sử cho mobile
  const renderMobileTable = () => (
    <Stack spacing={2}>
      {periodSummaries.map((period) => (
        <Card key={period._id} sx={{ border: '1px solid #e0e0e0' }}>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="subtitle1" fontWeight="bold" color="primary">
                {getPeriodName(period.periodStart)} ({new Date(period.periodStart).getFullYear()})
              </Typography>
              <Chip 
                label={`${period.totalHours}h`} 
                color="primary" 
                size="small" 
              />
            </Box>
            <Stack spacing={1}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="body2" color="text.secondary">
                  Thời gian:
                </Typography>
                <Typography variant="body2">
                  {formatDate(period.periodStart)} - {formatDate(period.periodEnd)}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="body2" color="text.secondary">
                  Giờ công:
                </Typography>
                <Typography variant="body2" fontWeight="bold">
                  {period.totalHours} giờ
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="body2" color="text.secondary">
                  Lương/giờ:
                </Typography>
                <Typography variant="body2">
                  {formatCurrency(period.hourlyRate)}
                </Typography>
              </Box>
              <Divider />
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="body2" color="text.secondary">
                  Tổng thu nhập:
                </Typography>
                <Box>
                  <Typography variant="body1" fontWeight="bold" color="success.main" align="right">
                    {formatCurrency(period.totalEarnings)}
                  </Typography>
                  {renderPolicyExtras(period)}
                </Box>
              </Box>
              <Divider />
              <Button
                size="small"
                onClick={() => setExpandedId(expandedId === period._id ? null : period._id)}
                endIcon={expandedId === period._id ? <CollapseIcon /> : <ExpandIcon />}
                sx={{ alignSelf: 'flex-start', textTransform: 'none' }}
              >
                {expandedId === period._id ? 'Ẩn chi tiết' : 'Xem chi tiết cấu thành'}
              </Button>
              <Collapse in={expandedId === period._id} timeout="auto" unmountOnExit>
                {renderBreakdown(period)}
              </Collapse>
            </Stack>
          </CardContent>
        </Card>
      ))}
    </Stack>
  );

  // Render bảng lịch sử cho desktop
  const renderDesktopTable = () => (
    <TableContainer component={Paper} sx={{ maxHeight: 400 }}>
      <Table stickyHeader>
        <TableHead>
          <TableRow>
            <TableCell sx={{ fontWeight: 'bold', bgcolor: '#f5f5f5' }}>
              Kỳ
            </TableCell>
            <TableCell sx={{ fontWeight: 'bold', bgcolor: '#f5f5f5' }}>
              Thời gian
            </TableCell>
            <TableCell align="center" sx={{ fontWeight: 'bold', bgcolor: '#f5f5f5' }}>
              Giờ live
            </TableCell>
            <TableCell align="right" sx={{ fontWeight: 'bold', bgcolor: '#f5f5f5' }}>
              Lương/giờ
            </TableCell>
            <TableCell align="right" sx={{ fontWeight: 'bold', bgcolor: '#f5f5f5' }}>
              Tổng thu nhập
            </TableCell>
            <TableCell align="center" sx={{ fontWeight: 'bold', bgcolor: '#f5f5f5', width: 60 }}>
              Chi tiết
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {periodSummaries.map((period) => {
            const isOpen = expandedId === period._id;
            return (
              <React.Fragment key={period._id}>
                <TableRow hover sx={{ cursor: 'pointer', '& > td': { borderBottom: isOpen ? 'unset' : undefined } }} onClick={() => setExpandedId(isOpen ? null : period._id)}>
                  <TableCell>
                    <Chip
                      label={`${getPeriodName(period.periodStart)} ${new Date(period.periodStart).getFullYear()}`}
                      color="primary"
                      variant="outlined"
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {formatDate(period.periodStart)} - {formatDate(period.periodEnd)}
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Typography variant="body2" fontWeight="bold">
                      {period.totalHours} giờ
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Typography variant="body2">
                      {formatCurrency(period.hourlyRate)}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Typography variant="body1" fontWeight="bold" color="success.main">
                      {formatCurrency(period.totalEarnings)}
                    </Typography>
                    {renderPolicyExtras(period)}
                  </TableCell>
                  <TableCell align="center">
                    <IconButton size="small" onClick={(e) => { e.stopPropagation(); setExpandedId(isOpen ? null : period._id); }}>
                      {isOpen ? <CollapseIcon /> : <ExpandIcon />}
                    </IconButton>
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell colSpan={6} sx={{ py: 0, borderBottom: isOpen ? undefined : 'none' }}>
                    <Collapse in={isOpen} timeout="auto" unmountOnExit>
                      <Box sx={{ py: 1.5 }}>{renderBreakdown(period)}</Box>
                    </Collapse>
                  </TableCell>
                </TableRow>
              </React.Fragment>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );

  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      maxWidth="lg" 
      fullWidth
      fullScreen={isMobile}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box>
            <Typography variant="h6" component="div">
              📊 Lịch sử thu nhập kỳ live
            </Typography>
            <Typography variant="subtitle2" color="text.secondary">
              {userName}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Tooltip title="Làm mới dữ liệu">
              <IconButton onClick={fetchEarningsHistory} disabled={loading} size="small">
                <RefreshIcon />
              </IconButton>
            </Tooltip>
            <IconButton onClick={onClose} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ px: 3 }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        ) : periodSummaries.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <CalendarIcon sx={{ fontSize: 48, color: '#e0e0e0', mb: 2 }} />
            <Typography variant="h6" color="text.secondary" gutterBottom>
              Chưa có lịch sử thu nhập theo kỳ
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Thu nhập sẽ được tự động lưu vào ngày 10 hàng tháng (cuối kỳ).
            </Typography>
            <Alert severity="info" sx={{ mt: 2, maxWidth: 400, mx: 'auto' }}>
              <Typography variant="body2">
                <strong>Chu kỳ tính lương:</strong> Từ ngày 10 tháng trước đến ngày 9 tháng này.
                Hệ thống sẽ tự động lưu thu nhập khi hết kỳ.
              </Typography>
            </Alert>
          </Box>
        ) : (
          <>
            {/* Thống kê tổng quan */}
            {renderStatsCards()}

            {/* Bảng lịch sử */}
            <Typography variant="subtitle1" gutterBottom sx={{ mt: 2, fontWeight: 'bold' }}>
              Chi tiết theo từng kỳ ({periodSummaries.length} kỳ)
            </Typography>
            
            {isMobile ? renderMobileTable() : renderDesktopTable()}
          </>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} variant="outlined">
          Đóng
        </Button>
      </DialogActions>

      {/* Điều chỉnh 1 khoản thưởng tự động của kỳ */}
      <Dialog open={!!adjusting} onClose={() => setAdjusting(null)} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ fontWeight: 700 }}>Điều chỉnh khoản thưởng</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" sx={{ mb: 0.5 }}>{adjusting?.label}</Typography>
          <Typography variant="caption" color="text.secondary" component="div" sx={{ mb: 2 }}>
            Hệ thống tính: {formatCurrency(adjusting?.original || 0)}
          </Typography>
          <TextField
            fullWidth size="small" autoFocus type="number" label="Số tiền (đ)"
            value={adjusting?.amount ?? ''}
            onChange={e => setAdjusting(prev => ({ ...prev, amount: e.target.value }))}
            helperText="Điền 0 để bỏ hẳn khoản này khỏi bảng lương"
          />
          <TextField
            fullWidth size="small" sx={{ mt: 2 }} label="Lý do"
            value={adjusting?.note ?? ''}
            onChange={e => setAdjusting(prev => ({ ...prev, note: e.target.value }))}
            placeholder="VD: đã trả tay ở kỳ trước"
          />
          <Alert severity="info" variant="outlined" sx={{ mt: 2, borderRadius: 2 }}>
            Điều chỉnh được lưu riêng nên <b>không bị mất</b> khi hệ thống chốt lại kỳ. Bấm khôi phục bất cứ lúc nào để quay về số hệ thống tính.
          </Alert>
        </DialogContent>
        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button variant="outlined" onClick={() => setAdjusting(null)} sx={{ textTransform: 'none', borderRadius: 2 }}>Huỷ</Button>
          <Button variant="contained" onClick={saveAdjust} disabled={savingAdjust} sx={{ textTransform: 'none', borderRadius: 2 }}>
            {savingAdjust ? <CircularProgress size={18} color="inherit" /> : 'Lưu'}
          </Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
};

export default UserEarningsHistory; 